/**
 * Controllers Service - Irrigo Platform
 * Servicio para gestión de controladores y válvulas
 */

import { api, ApiResponse, PaginatedResponse, PaginationParams } from './api';
import type { 
  ApiController, 
  CreateControllerRequest, 
  UpdateControllerRequest,
  ValveActionRequest,
  ValveActionResponse,
  UpdateValveRequest,
  ValveTimelineResponse,
  ControllerOperationMode,
  CommandType,
  CommandStatus,
  ApiCommandHistory,
  ApiCommandStats,
  ApiBatteryHistory,
  ApiBatteryTrend,
  ApiBatteryStats,
  ApiControllerLocalMode,
  SetLocalModeRequest,
  SetSupervisionModeRequest,
  ConfigureLocalModeRequest,
} from '@/types/api';

// ============================================================================
// TYPES
// ============================================================================

export interface ControllerFilters extends PaginationParams {
  // CAMBIO v0.2.0: Filtrar por empresa en lugar de jardín
  companyId?: string;
  // Filtrar por zona (para obtener controllers asignados a una zona específica)
  zoneId?: string;
  status?: 'online' | 'offline' | 'warning';
  communicationType?: 'mqtt' | 'chirpstack';
  search?: string;
}

// ============================================================================
// CONTROLLERS SERVICE
// ============================================================================

export const controllersService = {
  /**
   * Listar controladores
   */
  async getAll(params?: ControllerFilters): Promise<PaginatedResponse<ApiController>> {
    return api.getPaginated<ApiController>('/controllers', params);
  },

  /**
   * Obtener controlador por ID
   */
  async getById(id: string): Promise<ApiResponse<ApiController>> {
    return api.get<ApiController>(`/controllers/${id}`);
  },

  /**
   * Crear nuevo controlador
   */
  async create(data: CreateControllerRequest): Promise<ApiResponse<ApiController>> {
    return api.post<ApiController>('/controllers', data);
  },

  /**
   * Actualizar controlador
   */
  async update(id: string, data: UpdateControllerRequest): Promise<ApiResponse<ApiController>> {
    return api.put<ApiController>(`/controllers/${id}`, data);
  },

  /**
   * Eliminar controlador
   */
  async delete(id: string): Promise<ApiResponse<{ message: string }>> {
    return api.delete<{ message: string }>(`/controllers/${id}`);
  },

  // ==========================================================================
  // VALVE ACTIONS
  // ==========================================================================

  /**
   * Ejecutar acción en válvula
   */
  async valveAction(controllerId: string, channel: number, data: ValveActionRequest): Promise<ApiResponse<ValveActionResponse>> {
    return api.post<ValveActionResponse>(`/controllers/${controllerId}/valves/${channel}/action`, data);
  },

  /**
   * Abrir válvula
   */
  async openValve(controllerId: string, channel: number, duration?: number): Promise<ApiResponse<ValveActionResponse>> {
    return this.valveAction(controllerId, channel, { action: 'open', duration });
  },

  /**
   * Cerrar válvula
   */
  async closeValve(controllerId: string, channel: number): Promise<ApiResponse<ValveActionResponse>> {
    return this.valveAction(controllerId, channel, { action: 'close' });
  },

  /**
   * Actualizar nombre de válvula
   */
  async updateValve(controllerId: string, valveId: string, data: UpdateValveRequest): Promise<ApiResponse<{ message: string }>> {
    return api.put<{ message: string }>(`/controllers/${controllerId}/valves/${valveId}`, data);
  },

  // ==========================================================================
  // OPERATION MODE
  // ==========================================================================

  /**
   * Cambiar modo de operación del controlador
   * - 'local': Usa programaciones locales del dispositivo
   * - 'manual': Solo acepta comandos manuales desde la plataforma
   * - 'remote': Recibe eventos programados desde la plataforma (default)
   * 
   * IMPORTANTE: Según la API, se deben enviar también localMode y supervisionMode
   */
  async setOperationMode(
    controllerId: string, 
    operationMode: ControllerOperationMode,
    options?: { localMode?: boolean; supervisionMode?: boolean }
  ): Promise<ApiResponse<ApiController>> {
    const data: any = { operationMode };
    
    // Si se proporciona localMode, incluirlo
    if (options?.localMode !== undefined) {
      data.localMode = options.localMode;
    } else {
      // Si no se proporciona, establecer según operationMode
      data.localMode = operationMode === 'local';
    }
    
    // Si se proporciona supervisionMode, incluirlo (solo válido si localMode es true)
    if (options?.supervisionMode !== undefined && data.localMode) {
      data.supervisionMode = options.supervisionMode;
    } else if (data.localMode === false) {
      // Si localMode es false, supervisionMode debe ser false
      data.supervisionMode = false;
    }
    
    return api.put<ApiController>(`/controllers/${controllerId}`, data);
  },

  // ==========================================================================
  // VALVE TIMELINE / HISTORY
  // ==========================================================================

  /**
   * Obtener historial de estados de válvulas de una controladora
   */
  async getValvesTimeline(
    controllerId: string, 
    startDate: string, 
    endDate: string
  ): Promise<ApiResponse<ValveTimelineResponse>> {
    return api.get<ValveTimelineResponse>(
      `/controllers/${controllerId}/valves/timeline`,
      { startDate, endDate }
    );
  },

  // ==========================================================================
  // COMMAND HISTORY
  // ==========================================================================

  /**
   * Obtener historial de comandos enviados a una controladora
   */
  async getCommands(
    controllerId: string,
    params?: {
      page?: number;
      limit?: number;
      valveId?: string;
      type?: CommandType;
      status?: CommandStatus;
      startDate?: string;
      endDate?: string;
    }
  ): Promise<PaginatedResponse<ApiCommandHistory>> {
    return api.getPaginated<ApiCommandHistory>(`/controllers/${controllerId}/commands`, params);
  },

  /**
   * Obtener estadísticas de comandos
   */
  async getCommandsStats(
    controllerId: string,
    since?: string
  ): Promise<ApiResponse<ApiCommandStats>> {
    return api.get<ApiCommandStats>(`/controllers/${controllerId}/commands/stats`, since ? { since } : undefined);
  },

  /**
   * Obtener comandos pendientes
   */
  async getPendingCommands(controllerId: string): Promise<ApiResponse<ApiCommandHistory[]>> {
    return api.get<ApiCommandHistory[]>(`/controllers/${controllerId}/commands/pending`);
  },

  // ==========================================================================
  // BATTERY HISTORY
  // ==========================================================================

  /**
   * Obtener historial de batería
   */
  async getBatteryHistory(
    controllerId: string,
    params?: {
      page?: number;
      limit?: number;
      startDate?: string;
      endDate?: string;
    }
  ): Promise<PaginatedResponse<ApiBatteryHistory>> {
    return api.getPaginated<ApiBatteryHistory>(`/controllers/${controllerId}/battery`, params);
  },

  /**
   * Obtener tendencia de batería para gráficos
   */
  async getBatteryTrend(
    controllerId: string,
    since?: string,
    until?: string
  ): Promise<ApiResponse<ApiBatteryTrend[]>> {
    return api.get<ApiBatteryTrend[]>(`/controllers/${controllerId}/battery/trend`, { since, until });
  },

  /**
   * Obtener estadísticas de batería
   */
  async getBatteryStats(controllerId: string): Promise<ApiResponse<ApiBatteryStats>> {
    return api.get<ApiBatteryStats>(`/controllers/${controllerId}/battery/stats`);
  },

  // ==========================================================================
  // REBOOT & SEQUENCE MANAGEMENT
  // ==========================================================================

  /**
   * Reiniciar controlador (envía comando FF 10 FF al dispositivo Milesight)
   * También resetea el command_sequence a 0 en la BD.
   * Usar cuando el controlador no responde a comandos por pérdida de sincronización.
   */
  async reboot(controllerId: string): Promise<ApiResponse<RebootResponse>> {
    return api.post<RebootResponse>(`/controllers/${controllerId}/reboot`);
  },

  /**
   * Resetear manualmente el command_sequence sin enviar comando al dispositivo.
   * Usar cuando el dispositivo fue reiniciado externamente y necesitas sincronizar.
   * @param sequence - Valor del sequence (default: 0, próximo comando usará seq=1)
   */
  async resetSequence(controllerId: string, sequence?: number): Promise<ApiResponse<ResetSequenceResponse>> {
    return api.post<ResetSequenceResponse>(
      `/controllers/${controllerId}/reset-sequence`,
      sequence !== undefined ? { sequence } : undefined
    );
  },

  // ==========================================================================
  // LOCAL MODE METHODS (v0.2)
  // ==========================================================================

  /**
   * Listar controladoras en modo local
   */
  async getLocalModeControllers(): Promise<ApiResponse<ApiControllerLocalMode[]>> {
    return api.get<ApiControllerLocalMode[]>('/controllers/local-mode');
  },

  /**
   * Listar controladoras con supervisión activa
   */
  async getSupervisionControllers(): Promise<ApiResponse<ApiControllerLocalMode[]>> {
    return api.get<ApiControllerLocalMode[]>('/controllers/supervision');
  },

  /**
   * Activar o desactivar modo local
   */
  async setLocalMode(
    id: string,
    data: SetLocalModeRequest
  ): Promise<ApiResponse<{ localMode: boolean }>> {
    return api.put<{ localMode: boolean }>(`/controllers/${id}/local-mode`, data);
  },

  /**
   * Activar o desactivar supervisión (solo en modo local)
   */
  async setSupervisionMode(
    id: string,
    data: SetSupervisionModeRequest
  ): Promise<ApiResponse<{ supervisionMode: boolean }>> {
    return api.put<{ supervisionMode: boolean }>(`/controllers/${id}/supervision-mode`, data);
  },

  /**
   * Configurar modo local completo (timezone, protocolo, etc.)
   */
  async configureLocalMode(
    id: string,
    data: ConfigureLocalModeRequest
  ): Promise<ApiResponse<ConfigureLocalModeRequest>> {
    return api.put<ConfigureLocalModeRequest>(`/controllers/${id}/local-mode-config`, data);
  },

  // ==========================================================================
  // TIME SYNC METHODS (v0.2)
  // ==========================================================================

  /**
   * Sincronizar hora de controladora individual
   */
  async syncTime(controllerId: string): Promise<ApiResponse<{ message: string }>> {
    return api.post<{ message: string }>(`/controllers/${controllerId}/time-sync`);
  },

  /**
   * Sincronizar hora de TODAS las controladoras en modo local
   * Solo disponible para superadmin
   */
  async syncTimeAll(): Promise<ApiResponse<{
    message: string;
    successCount: number;
    errorCount: number;
  }>> {
    return api.post<{
      message: string;
      successCount: number;
      errorCount: number;
    }>('/controllers/time-sync');
  },
};

// ============================================================================
// RESPONSE TYPES
// ============================================================================

export interface RebootResponse {
  controllerId: string;
  controllerName: string;
  deviceEUI: string;
  status: 'sent' | 'error';
  message: string;
  sequenceReset: boolean;
}

export interface ResetSequenceResponse {
  controllerId: string;
  controllerName: string;
  previousSequence: number;
  newSequence: number;
  message: string;
}

export default controllersService;
